import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Client } from '../../http';
import { RootState } from '../../app/store';

export type Dimension = 'none' | 'utm_source' | 'utm_medium' | 'utm_campaign';

export interface SignupStatsBucket {
  date: string;
  series: Record<string, number>;
}

// Components of a composite series key ("keyring — 3006.DRCS.ST.22"), provided
// by the backend so the client never parses the key string.
export interface SignupSeriesMeta {
  utm: string;
  building?: string;
}

export interface SignupStatsResponse {
  from: string;
  to: string;
  dimension: Dimension;
  buckets: SignupStatsBucket[];
  series_meta?: Record<string, SignupSeriesMeta>;
}

export interface SignupStatsArgs {
  from: string;
  to: string;
  dimension: Dimension;
}

const cacheKey = (args: SignupStatsArgs) => `${args.from}|${args.to}|${args.dimension}`;

export interface SignupStatsState {
  byKey: Record<string, SignupStatsResponse>;
  status: 'idle' | 'loading' | 'failed';
  lastKey: string | null;
  error: string | null;
}

const initialState: SignupStatsState = {
  byKey: {},
  status: 'idle',
  lastKey: null,
  error: null,
};

export const fetchSignupStats = createAsyncThunk(
  'signupStats/fetch',
  async (args: SignupStatsArgs) => {
    const params = new URLSearchParams({
      from: args.from,
      to: args.to,
      dimension: args.dimension,
    });
    const response = await Client.get<SignupStatsResponse>(
      `/api/v1/admin/accounts/signup-stats?${params.toString()}`,
    );
    return { key: cacheKey(args), data: response.data };
  },
  {
    condition: (args, { getState }) => {
      const state = (getState() as RootState).signupStats;
      return !state.byKey[cacheKey(args)] && state.status !== 'loading';
    },
  },
);

export const signupStatsSlice = createSlice({
  name: 'signupStats',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSignupStats.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSignupStats.fulfilled, (state, action) => {
        state.status = 'idle';
        state.byKey[action.payload.key] = action.payload.data;
        state.lastKey = action.payload.key;
      })
      .addCase(fetchSignupStats.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'failed to load signup stats';
      });
  },
});

export const selectSignupStats = (args: SignupStatsArgs) => (state: RootState) => (
  state.signupStats.byKey[cacheKey(args)]
);

export const selectSignupStatsStatus = (state: RootState) => state.signupStats.status;
export const selectSignupStatsError = (state: RootState) => state.signupStats.error;

export default signupStatsSlice.reducer;
